<template>
  <div class="bingo-scoreboard">
    <table class="bingo-scoreboard-table">
      <thead>
        <th
          v-for="(key, index) in scoreboardKeys"
          :key="index"
          class="bingo-scoreboard-table-header"
        >
          {{ key }}
        </th>
      </thead>
      <tbody>
        <tr
          v-for="(score, indexColumn) in scoreboard"
          :key="indexColumn"
          class="bingo-scoreboard-table-column"
        >
          <td
            v-for="(scoreElement, indexRow) in score"
            :key="indexRow"
            class="bingo-scoreboard-table-row"
          >
            {{ scoreElement }}
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script setup lang="ts">
import { ref, inject, Ref, onMounted } from 'vue';
import BingoService from '@/services/BingoService';
import { BingoScoreClass, IBingoScore } from 'interface/bingoScore';
import { Logger } from '@/middlewares/sentry';

interface DialogObject {
  data: {
    mainColor: string;
    buttonColor: string;
    backgroundColor: string;
  };
}

const defaultColors = {
  mainColor: '#000000',
  buttonColor: '#000000',
  backgroundColor: '#ffffff',
};

const dialogRef =
  inject<Ref<DialogObject>>('dialogRef') ?? ref({ data: defaultColors });

const mainColor = ref(dialogRef.value.data.mainColor);

const scoreboard = ref<Array<IBingoScore>>();
const scoreboardKeys = ref<Array<string>>([]);

onMounted(async () => {
  let scoreboardData: IBingoScore[] = [];
  try {
    scoreboardData = Object.values(await BingoService.getScoreboard());
  } catch (err) {
    Logger.warn("Scoreboard don't found");
  }

  scoreboardData.sort((a, b) => {
    if (b['fullBingoCount'] !== a['fullBingoCount']) {
      return b['fullBingoCount'] - a['fullBingoCount'];
    } else if (b['completedTasksNum'] !== a['completedTasksNum']) {
      return b['completedTasksNum'] - a['completedTasksNum'];
    } else {
      return (a['updateTime'] as number) - (b['updateTime'] as number);
    }
  });

  let lp = 1;
  scoreboard.value = scoreboardData.map((score: IBingoScore) => {
    const formattedScore = {
      lp,
      ...score,
      updateTime: new Date(score['updateTime'] as number).toLocaleString(),
    };
    lp += 1;

    return formattedScore;
  });

  const translations = {
    lp: 'Lp.',
    playerId: 'Lp.',
    updateTime: 'Czas wykonania ostatniego zadania',
    playerName: 'Nazwa gracza',
    bingoCount: 'Liczba bingo',
    fullBingoCount: 'Liczba pełnych bingo',
    completedTasksNum: 'Liczba ukończonych zadań',
  };
  scoreboardKeys.value = Object.keys(
    scoreboard.value[0] ?? new BingoScoreClass(),
  ).map((key) => {
    if (translations[key]) {
      return translations[key];
    }
    return key;
  });
});
</script>

<style scoped lang="scss">
@import '@/assets/styleConstans';

.bingo-scoreboard {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;

  &-table {
    width: 100%;
    border-radius: 10px;
    border-collapse: separate;
    border-spacing: 0;
    overflow: hidden;

    @media (max-width: $media-phone) {
      padding-right: var(--p-overlay-modal-padding);
    }

    &-header,
    &-row {
      border-left: 1px solid v-bind(mainColor);
      border-top: 1px solid v-bind(mainColor);
      text-align: left;
      padding: 8px;
    }

    &-header {
      font-weight: bold;
    }

    &-header:first-child {
      border-top-left-radius: 10px;
    }

    &-header:last-child {
      border-top-right-radius: 10px;
      border-right: 1px solid v-bind(mainColor);
    }

    &-row:last-child {
      border-right: 1px solid v-bind(mainColor);
    }

    &-column:last-child &-row {
      border-bottom: 1px solid v-bind(mainColor);
    }

    &-column:last-child &-row:first-child {
      border-bottom-left-radius: 10px;
    }

    &-column:last-child &-row:last-child {
      border-bottom-right-radius: 10px;
    }
  }
}
</style>
